import { AnimatePresence } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import { FaRegEye, FaRegEyeSlash, FaTelegramPlane } from 'react-icons/fa'
import { RiUserLine } from 'react-icons/ri'
import { useLocation, useNavigate } from 'react-router-dom'
import { ROUTES } from '../../../routing/routes'
import Logo from '../../../styles/icons/contact-center-logo.svg'
import { useAccessibilityMode } from '../../../utils/use.accessibility.mode'
import useWindowWidth from '../../../utils/use.window.width'
import { LoginModalComponent } from '../../ui/modals/login.modal.component'
import { NotificationsComponent } from '../../ui/notifications.component'
import * as styled from './styled'

export const mainMenuItems = [
	{
		name: 'Інфо-центр',
		route: null,
		subItems: [
			{ name: 'Новини', route: ROUTES.NEWS },
			{ name: 'Нормативна база', route: ROUTES.NORMATIVE_BASE },
		],
	},
	{ name: 'Ремонтні роботи', route: ROUTES.REPAIR_WORK },
	{ name: 'Мапа звернень', route: ROUTES.REQUESTS, },
	{ name: 'Статистика', route: ROUTES.STATISTICS },
	{ name: 'Мапа Укриттів', route: 'https://ukryttya.zp.gov.ua/' },
]

export const HeaderComponent: React.FC = () => {
	const [mobileOpen, setMobileOpen] = useState(false)
	const [mobileDropOpen, setMobileDropOpen] = useState(false)
	const [isLoginOpen, setIsLoginOpen] = useState(false)
	const location = useLocation()
	const navigate = useNavigate()

	const windowWidth = useWindowWidth()
	const isTablet = windowWidth <= 1024

	const [activeItem, setActiveItem] = useState<number | null>(() => {
		const index = mainMenuItems.findIndex(
			item => item.route && item.route === location.pathname,
		)
		return index !== -1 ? index : null
	})
	const [openDropdown, setOpenDropdown] = useState<number | null>(null)
	const menuRef = useRef<HTMLDivElement>(null)

	const { isAccessible, enableAccessibility, disableAccessibility } =
		useAccessibilityMode()

	useEffect(() => {
		const currentIndex = mainMenuItems.findIndex(item => {
			if (item.route && item.route === location.pathname) return true
			if (item.subItems) {
				return item.subItems.some(sub => sub.route === location.pathname)
			}
			return false
		})
		setActiveItem(currentIndex !== -1 ? currentIndex : null)
	}, [location.pathname])

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
				setOpenDropdown(null)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const handleMenuItemClick = (index: number, e: React.MouseEvent) => {
		const item = mainMenuItems[index]

		if (item.subItems) {
			e.preventDefault()
			setOpenDropdown(openDropdown === index ? null : index)
		} else if (item.route) {
			if (item.route.startsWith('http')) {
				e.preventDefault()
				e.stopPropagation()
				window.open(item.route, '_blank', 'noopener,noreferrer')
				return
			}

			setActiveItem(index)
			setOpenDropdown(null)
			navigate(item.route)
		}
	}

	return (
		<>
			<styled.Header>
				<styled.HeaderTop>
					<styled.HeaderTopLinks>
						<styled.LinkContainer>
							<styled.LinkText>
								Вебпортал працює у тестовому режимі
							</styled.LinkText>
							<styled.LinkText>/</styled.LinkText>
							<styled.LinkItem
								as='a'
								href='https://t.me/colcentrebot'
								target='_blank'
								rel='noopener noreferrer'
							>
								<FaTelegramPlane color='#fff' size={15} />
								<styled.LinkText>Запоріжжя 1580</styled.LinkText>
							</styled.LinkItem>
						</styled.LinkContainer>
					</styled.HeaderTopLinks>
				</styled.HeaderTop>
				<styled.HeaderWrappper>
					<styled.HeaderContainer>
						<styled.HeaderActions>
							<styled.BurgerButton
								className={mobileOpen ? 'open' : ''}
								onClick={() => setMobileOpen(v => !v)}
							>
								<span />
								<span />
								<span />
							</styled.BurgerButton>
							<styled.HeaderLogo
								src={Logo}
								alt='logo'
								onClick={() => {
									navigate(ROUTES.HOME)
								}}
							/>
						</styled.HeaderActions>
						<styled.HeaderMenuList ref={menuRef}>
							{mainMenuItems.map((item, index) => (
								<styled.MenuListItem
									key={item.name}
									$active={index === activeItem}
									//$isDev={item.isDevelopment}
									onClick={e => handleMenuItemClick(index, e)}
								>
									{item.name}
									{/*{item.isDevelopment && (
										<styled.Tooltip>В розробці</styled.Tooltip>
									)} */}
									{item.subItems && (
										<styled.ArrowIcon $isOpen={openDropdown === index} />
									)}

									{item.subItems && (
										<styled.DropdownMenu $isOpen={openDropdown === index}>
											{item.subItems.map(sub => (
												<styled.DropdownItem
													key={sub.name}
													onClick={e => {
														e.stopPropagation()

														navigate(sub.route!)
														setOpenDropdown(null)
														setActiveItem(index)
													}}
												>
													{sub.name}
												</styled.DropdownItem>
											))}
										</styled.DropdownMenu>
									)}
								</styled.MenuListItem>
							))}
						</styled.HeaderMenuList>
						<styled.HeaderActions>
							<styled.AccessibilityIcons
								onClick={
									isAccessible ? disableAccessibility : enableAccessibility
								}
							>
								{isAccessible ? (
									<FaRegEye size={24} />
								) : (
									<FaRegEyeSlash size={24} />
								)}
							</styled.AccessibilityIcons>
							<styled.UserIconWrapper onClick={() => setIsLoginOpen(v => !v)}>
								<RiUserLine size={24} />
							</styled.UserIconWrapper>
							<AnimatePresence>
								{isLoginOpen && (
									<LoginModalComponent onClose={() => setIsLoginOpen(false)} />
								)}
							</AnimatePresence>
						</styled.HeaderActions>
					</styled.HeaderContainer>

					<styled.MobileDrawer $isOpen={mobileOpen}>
						{mainMenuItems.map((item, index) => (
							<React.Fragment key={item.name}>
								<styled.MobileMenuItem
									$active={index === activeItem}
									//$isDev={item.isDevelopment}
									onClick={e => {
										//if (item.isDevelopment) return

										if (item.subItems) {
											setMobileDropOpen(v => !v)
											return
										}

										if (item.route) {
											if (item.route.startsWith('http')) {
												window.open(item.route, '_blank', 'noopener,noreferrer')
												return
											}

											setActiveItem(index)
											setMobileOpen(false)
											navigate(item.route)
										}
									}}
								>
									{item.name}
									{/*{item.isDevelopment && (
										<styled.Tooltip>В розробці</styled.Tooltip>
									)} */}
									{item.subItems && (
										<styled.ArrowIcon
											$isOpen={mobileDropOpen}
											$active={index === activeItem}
										/>
									)}
								</styled.MobileMenuItem>
								{item.subItems &&
									mobileDropOpen &&
									item.subItems.map(sub => (
										<styled.MobileSubItem
											key={sub.name}
											onClick={() => {
												if (!sub.route) return
												navigate(sub.route)
												setMobileOpen(false)
												setMobileDropOpen(false)
												setActiveItem(index)
											}}
										>
											{sub.name}
										</styled.MobileSubItem>
									))}
							</React.Fragment>
						))}
					</styled.MobileDrawer>
				</styled.HeaderWrappper>
			</styled.Header>
			{isTablet && mobileOpen && (
				<styled.MobileOverlay onClick={() => setMobileOpen(false)} />
			)}

			<styled.NotificationHeaderContainer>
				<NotificationsComponent />
			</styled.NotificationHeaderContainer>
		</>
	)
}
